import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';

import { SelectOption } from '../../../sharedModules/form/model';
import { minimumLoading } from '../../../utils/minimumLoading';

import axios from '../../../sharedModules/utils/axios.utils';

interface UserPermission {
  id: number;
  user_id: number;
  user_name: string;
  permission_tier: number;
  can_review: boolean;
}

const AppModuleDetailPage: React.FC = () => {
  const { appModuleId } = useParams<{ appModuleId: string }>();

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [moduleName, setModuleName] = useState<string>('');
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [userOptions, setUserOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedPermissionTier, setSelectedPermissionTier] = useState<number>(1);

  const fetchModulePermissions = useCallback(async () => {
    try {
      const response: any = await axios.get(`/admin/app_modules/${appModuleId}`);
      setModuleName(response.data.name);
      setUserPermissions(response.data.user_permissions.filter((up: UserPermission) => up.permission_tier > 0));
      setUserOptions(response.data.user_options);
      setError('');
    } catch {
      setError('Something went wrong getting the App Module');
    }
  }, [appModuleId]);

  useEffect(() => {
    void minimumLoading(async () => {
      await fetchModulePermissions();
    }, setLoading);
  }, [fetchModulePermissions]);

  const handleAddUser = () => {
    if (selectedUserId === '') return;

    const existingPermission = userPermissions.find((up) => up.user_id === selectedUserId);
    if (existingPermission) {
      setError('User already has permissions for this module');
      return;
    }

    const selectedUser = userOptions.find((opt) => opt.value === selectedUserId);
    if (!selectedUser) return;

    const newPermission: UserPermission = {
      id: 0,
      user_id: selectedUserId as number,
      user_name: selectedUser.label,
      permission_tier: selectedPermissionTier,
      can_review: false,
    };

    setUserPermissions([...userPermissions, newPermission]);
    setSelectedUserId('');
    setSelectedPermissionTier(1);
  };

  const handleRemoveUser = (userId: number) => {
    setUserPermissions(userPermissions.filter((up) => up.user_id !== userId));
  };

  const handlePermissionTierChange = (userId: number, newTier: number) => {
    setUserPermissions(userPermissions.map((up) => (up.user_id === userId ? { ...up, permission_tier: newTier } : up)));
  };

  const handleCanReviewChange = (userId: number, canReview: boolean) => {
    setUserPermissions(userPermissions.map((up) => (up.user_id === userId ? { ...up, can_review: canReview } : up)));
  };

  const handleSavePermissions = async () => {
    try {
      await minimumLoading(async () => {
        await axios.put(`/admin/app_modules/${appModuleId}`, {
          user_permissions: userPermissions.map((up) => ({
            user_id: up.user_id,
            permission_tier: up.permission_tier,
            can_review: up.can_review,
          })),
        });
        await fetchModulePermissions();
      }, setLoading);

      setError('');
    } catch {
      setError('Something went wrong updating the App Module');
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={2} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle="Admin"
            subTitle={`App Module: ${moduleName}`}
            backButtonLabel="App Modules"
            backButtonLink="/admin/app_modules"
          />
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={error} />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="user-select-label">Select User</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={selectedUserId}
                    label="Select User"
                    onChange={(e) => setSelectedUserId(e.target.value as number | '')}
                  >
                    <MenuItem value="">
                      <em>Select a user...</em>
                    </MenuItem>
                    {userOptions
                      .filter((opt) => !userPermissions.find((up) => up.user_id === opt.value))
                      .map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="permission-tier-label">Permission Level</InputLabel>
                  <Select
                    labelId="permission-tier-label"
                    value={selectedPermissionTier}
                    label="Permission Level"
                    onChange={(e) => setSelectedPermissionTier(e.target.value as number)}
                  >
                    <MenuItem value={1}>Read Access</MenuItem>
                    <MenuItem value={2}>Read/Write</MenuItem>
                    <MenuItem value={3}>Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" onClick={handleAddUser} disabled={selectedUserId === ''} fullWidth>
                  Add User
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell>Permission Level</TableCell>
                  <TableCell align="center">Can Review/Approve</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userPermissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <em style={{ color: '#999' }}>No users assigned to this module</em>
                    </TableCell>
                  </TableRow>
                ) : (
                  userPermissions.map((userPerm) => (
                    <TableRow key={userPerm.user_id}>
                      <TableCell>{userPerm.user_name}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value={userPerm.permission_tier}
                            onChange={(e) => handlePermissionTierChange(userPerm.user_id, e.target.value as number)}
                          >
                            <MenuItem value={1}>Read Access</MenuItem>
                            <MenuItem value={2}>Read/Write</MenuItem>
                            <MenuItem value={3}>Admin</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={userPerm.can_review}
                          onChange={(e) => handleCanReviewChange(userPerm.user_id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => handleRemoveUser(userPerm.user_id)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSavePermissions}>
            Save Permissions
          </Button>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default AppModuleDetailPage;
